import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, Toggle } from 'common/components';
import { useGetTenantSmsPacing } from 'services/hooks/queries';
import { useUpdateTenantSmsPacing } from 'services/hooks/mutations';
import { Tenant } from '../../types';
import {
    DAILY_CAP_MAX,
    DAILY_CAP_MIN,
    DAILY_CAP_NO_CAP,
    isNoCapValue,
    validateDailyCapInput,
} from '../../smsPacingUtils';

export interface TenantSmsPacingModalProps {
    isOpen: boolean;
    tenant: Tenant | null;
    onClose: () => void;
    onSuccess: (message: string) => void;
    onError: (message: string) => void;
}

/**
 * Reads tenant SMS daily cap and lets a super admin change it. Only the
 * tenants.config.smsPacing.dailyCap key is written; no other credential
 * field is touched (contract on backend side).
 */
export const TenantSmsPacingModal: React.FC<TenantSmsPacingModalProps> = ({
    isOpen,
    tenant,
    onClose,
    onSuccess,
    onError,
}) => {
    const tenantId = isOpen ? tenant?.id : undefined;
    const { tenantSmsPacing, isTenantSmsPacingLoading, isError } = useGetTenantSmsPacing(tenantId);
    const { updateTenantSmsPacing, isPending } = useUpdateTenantSmsPacing();

    // The two parts of the form state. noCap mirrors the Toggle; capInput backs
    // the number field. On open or when the fetched value arrives, both are
    // seeded from the server-chosen value so the editor matches what is live.
    const [noCap, setNoCap] = useState<boolean>(false);
    const [capInput, setCapInput] = useState<string>('');
    const [inputError, setInputError] = useState<string>('');

    useEffect(() => {
        if (!isOpen) return;
        if (!tenantSmsPacing) return;
        if (isNoCapValue(tenantSmsPacing.dailyCap)) {
            setNoCap(true);
            setCapInput('');
        } else {
            setNoCap(false);
            setCapInput(String(tenantSmsPacing.dailyCap));
        }
        setInputError('');
    }, [isOpen, tenantSmsPacing]);

    const close = () => {
        setInputError('');
        onClose();
    };

    const sendUpdate = async (dailyCap: number | null, successMessage: string) => {
        if (!tenant) return;
        try {
            await updateTenantSmsPacing({ id: tenant.id, data: { dailyCap } });
            onSuccess(successMessage);
            close();
        } catch (error: unknown) {
            const err = error as { response?: { data?: { error?: string; message?: string } } };
            onError(
                err.response?.data?.error ||
                    err.response?.data?.message ||
                    'Failed to update the SMS daily cap. Please try again.'
            );
        }
    };

    const handleSave = async () => {
        if (noCap) {
            await sendUpdate(DAILY_CAP_NO_CAP, 'SMS daily cap updated to no cap.');
            return;
        }
        const validation = validateDailyCapInput(capInput);
        if (validation !== true) {
            setInputError(validation);
            return;
        }
        setInputError('');
        await sendUpdate(Number(capInput), `SMS daily cap updated to ${Number(capInput).toLocaleString()}.`);
    };

    const handleReset = async () => {
        await sendUpdate(null, 'SMS daily cap reset to the default.');
    };

    const tenantName = tenant?.name ?? '';
    const current = tenantSmsPacing;
    const currentLabel = (() => {
        if (!current) return '';
        if (isNoCapValue(current.dailyCap)) {
            return current.isDefault ? 'No cap (default)' : 'No cap';
        }
        const value = current.dailyCap.toLocaleString();
        return current.isDefault ? `${value} (default)` : value;
    })();

    const footer = (
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button variant='secondary' onClick={close} disabled={isPending}>
                Cancel
            </Button>
            <Button
                variant='outline-secondary'
                onClick={handleReset}
                disabled={isPending || isTenantSmsPacingLoading || !!current?.isDefault}
                title={current?.isDefault ? 'Already using the default' : undefined}
            >
                Reset to default
            </Button>
            <Button variant='primary' onClick={handleSave} disabled={isPending || isTenantSmsPacingLoading}>
                {isPending ? 'Saving...' : 'Save'}
            </Button>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={close}
            title={tenantName ? `SMS daily cap: ${tenantName}` : 'SMS daily cap'}
            size='sm'
            showDefaultFooter={false}
            footer={footer}
        >
            {isTenantSmsPacingLoading ? (
                <p>Loading current cap...</p>
            ) : isError ? (
                <p role='alert'>The current cap could not be loaded.</p>
            ) : (
                <div className='flex flex-col gap-md'>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--gray-700)' }}>
                        Current: {currentLabel || 'unknown'}
                    </p>
                    <div className='flex items-center gap-sm'>
                        <Toggle
                            checked={noCap}
                            onChange={(e) => {
                                setNoCap(e.target.checked);
                                setInputError('');
                            }}
                            aria-label='Toggle no cap'
                        />
                        <span className='text-sm text-gray-700'>No cap</span>
                    </div>
                    {!noCap && (
                        <Input
                            id='tenant-sms-daily-cap'
                            label='Max texts per day'
                            type='number'
                            value={capInput}
                            onChange={(e) => setCapInput(e.target.value)}
                            min={DAILY_CAP_MIN}
                            max={DAILY_CAP_MAX}
                            placeholder={`${DAILY_CAP_MIN} to ${DAILY_CAP_MAX.toLocaleString()}`}
                            error={!!inputError}
                            errorText={inputError}
                        />
                    )}
                </div>
            )}
        </Modal>
    );
};
